import { useState } from 'react'
import { useAuth } from '../store/auth'
import { ACCENTS, applyAccent, useSettings, type AccentName } from '../store/settings'
import { api } from '../api/client'

export function SettingsDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { service, setService, accent, setAccent, pageSize, setPageSize } = useSettings()
  const { session, setSession } = useAuth()
  const [identifier, setIdentifier] = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function login(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      const s = await api.login({ service, identifier, password })
      setSession(s)
      setPassword('')
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div
      className={`fixed inset-0 z-50 transition ${open ? 'pointer-events-auto' : 'pointer-events-none'}`}
      aria-hidden={!open}
    >
      <div
        className={`absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity ${
          open ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={onClose}
      />
      <aside
        className={`absolute right-0 top-0 h-full w-full max-w-md transform transition-transform ${
          open ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="glass-strong m-3 h-[calc(100%-1.5rem)] overflow-y-auto p-5">
          <div className="mb-5 flex items-center justify-between">
            <h2 className="text-lg font-bold">Settings</h2>
            <button className="glass-button" onClick={onClose} aria-label="close">
              ✕
            </button>
          </div>

          <section className="mb-6">
            <h3 className="mb-2 text-xs uppercase tracking-widest text-ink-300">ATProto service</h3>
            <input
              className="glass-input w-full"
              value={service}
              onChange={(e) => setService(e.target.value)}
              placeholder="https://bsky.social"
            />
          </section>

          <section className="mb-6">
            <h3 className="mb-2 text-xs uppercase tracking-widest text-ink-300">Accent</h3>
            <div className="flex gap-2">
              {(Object.keys(ACCENTS) as AccentName[]).map((name) => (
                <button
                  key={name}
                  type="button"
                  onClick={() => {
                    setAccent(name)
                    applyAccent(name)
                  }}
                  className={`h-8 w-8 rounded-full border-2 transition ${
                    accent === name ? 'scale-110 border-white' : 'border-white/20'
                  }`}
                  style={{ background: ACCENTS[name] }}
                  aria-label={name}
                />
              ))}
            </div>
          </section>

          <section className="mb-6">
            <h3 className="mb-2 text-xs uppercase tracking-widest text-ink-300">Page size</h3>
            <input
              type="number"
              min={1}
              max={100}
              className="glass-input w-32"
              value={pageSize}
              onChange={(e) => setPageSize(Math.max(1, Math.min(100, Number(e.target.value) || 50)))}
            />
            <p className="mt-2 text-xs text-ink-300">
              Records per request to the ATProto service. Max 100.
            </p>
          </section>

          <section className="mb-2">
            <h3 className="mb-2 text-xs uppercase tracking-widest text-ink-300">Authentication</h3>
            {session ? (
              <div className="glass flex items-center justify-between p-3">
                <div>
                  <div className="text-sm font-semibold">@{session.handle}</div>
                  <div className="font-mono text-[10px] text-ink-300">{session.did}</div>
                </div>
                <button className="glass-button" onClick={() => setSession(null)}>
                  Log out
                </button>
              </div>
            ) : (
              <form onSubmit={login} className="flex flex-col gap-2">
                <input
                  className="glass-input"
                  placeholder="handle or email"
                  autoComplete="username"
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                />
                <input
                  className="glass-input"
                  type="password"
                  placeholder="app password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                {error ? <div className="text-xs text-rose-400">{error}</div> : null}
                <button type="submit" className="glass-button glass-button--accent" disabled={busy}>
                  {busy ? 'Signing in…' : 'Sign in'}
                </button>
                <p className="mt-1 text-xs text-ink-300">
                  Use a Bluesky{' '}
                  <a
                    className="accent-text underline"
                    href="https://bsky.app/settings/app-passwords"
                    target="_blank"
                    rel="noreferrer"
                  >
                    app password
                  </a>
                  , not your main password.
                </p>
              </form>
            )}
          </section>
        </div>
      </aside>
    </div>
  )
}
